/**
 * cssFontFace generates a string representation of the css @font-face rule
 * Note that this is necessary to make sure that we load all the fonts we need
 *
 * It was brought up here: https://github.com/mui-org/material-ui/issues/24966#issuecomment-780045031
 * and was resolved in: https://github.com/mui-org/material-ui/pull/25583
 */
const cssFontFace = (
  name,
  src,
  fontWeight = 'normal',
  fontStyle = 'normal'
) => `
@font-face {
  font-family: ${name};
  src: url(https://media-cdn.getbento.com/fonts/circular/${src}.eot?#iefix) format('embedded-opentype'),
       url(https://media-cdn.getbento.com/fonts/circular/${src}.otf) format('opentype'),
       url(https://media-cdn.getbento.com/fonts/circular/${src}.woff) format('woff'),
       url(https://media-cdn.getbento.com/fonts/circular/${src}.woff2) format('woff2'),
       url(https://media-cdn.getbento.com/fonts/circular/${src}.ttf) format('truetype'),
       url(https://media-cdn.getbento.com/fonts/circular/${src}.svg#${name}) format('svg');
  font-style: ${fontStyle};
  font-weight: ${fontWeight};
  font-display: swap;
}
`;

// TODO: To deprecate as we'll return back to assigning fontFamily instead of weight
const fontWeights = {
  regular: 400,
  italic: 300,
  bold: 700,
  medium: 500
};

// TODO: To deprecate since we'll be assigning font family on a component basis
export const fontFamily = 'CircularStd';

const BASE_MEDIA_CDN = '//media-cdn.getbento.com/';

/**
 * This function generates font-face strings for self-hosted fonts
 * that will be loaded into the MUI theme.
 *
 * @param {object[]} selfHostedFonts
 * @returns {string[]}
 */
export function loadSelfHostedCssFontFaces(selfHostedFonts) {
  if (!selfHostedFonts || !selfHostedFonts.length) {
    return [];
  }

  return selfHostedFonts.map(
    ({
      selfhosted_name: fontName,
      selfhosted_style: fontStyle,
      selfhosted_weight: fontWeight,
      selfhosted_eot: eot,
      selfhosted_woff2: woff2,
      selfhosted_woff: woff,
      selfhosted_ttf: ttf,
      selfhosted_otf: otf
    }) => {
      const urls = [];

      if (eot) {
        urls.push(
          `url("${BASE_MEDIA_CDN}${eot}#iefix") format("embedded-opentype")`
        );
      }

      if (woff2) {
        urls.push(`url("${BASE_MEDIA_CDN}${woff2}") format("woff2")`);
      }

      if (woff) {
        urls.push(`url("${BASE_MEDIA_CDN}${woff}") format("woff")`);
      }

      if (ttf) {
        urls.push(`url("${BASE_MEDIA_CDN}${ttf}") format("truetype")`);
      }

      if (otf) {
        urls.push(`url("${BASE_MEDIA_CDN}${otf}") format("opentype")`);
      }

      return `
      @font-face {
        font-family: "${fontName}";
        font-style: ${fontStyle ?? 'normal'};
        font-weight: ${fontWeight ?? 400};
        font-display: swap;
        src: ${urls.join(', ')};
      }
    `;
    }
  );
}

/**
 * This function creates the string of brand fonts to load if any
 *
 * @returns {string[]}
 */
export function loadBrandFont() {
  return [
    cssFontFace('CircularStd-Book', 'CircularStd-Book', 400),
    cssFontFace('CircularStd-Medium', 'CircularStd-Medium', 500),
    cssFontFace('CircularStd-Bold', 'CircularStd-Bold', 700),
    cssFontFace('CircularStd-Book', 'CircularStd-BookItalic', 300, 'italic')
  ];
}

/**
 * This function loads default brand fonts for catering pre-D&C
 *
 * @returns {string[]}
 */
export function loadDefaultBrandFont() {
  return [
    cssFontFace('CircularStd', 'CircularStd-Book', 400),
    cssFontFace('CircularStd', 'CircularStd-Medium', 500),
    cssFontFace('CircularStd', 'CircularStd-Bold', 700),
    cssFontFace('CircularStd', 'CircularStd-BookItalic', 300, 'italic')
  ];
}

export default fontWeights;
